import { gql, useQuery } from '@apollo/client';
import IconGraphQL from '@apollo/icons/default/IconGraphQL.svg';
import IconRocket from '@apollo/icons/default/IconRocket.svg';
import IconSuccessSolid from '@apollo/icons/default/IconSuccessSolid.svg';
import {
  Button,
  ButtonGroup,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  Spinner,
} from '@apollo/orbit';
import React from 'react';
import { Link } from 'react-router-dom';

import { launchesRouteConfig } from 'src/app/graph/routes';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import {
  LatestLaunchDetailsQuery,
  LatestLaunchDetailsQueryVariables,
} from 'src/lib/graphqlTypes/types';
import { useLDFlag } from 'src/lib/launchDarkly';

import { ContractConfigHeader } from '../ContractConfigHeader';
import { ContractConfigStep, ContractConfigWorkflow } from '../steps';

import { DefinitionList } from './DefinitionList';

const latestLaunchDetailsQuery = gql<
  LatestLaunchDetailsQuery,
  LatestLaunchDetailsQueryVariables
>`
  query LatestLaunchDetailsQuery($graphId: ID!, $variant: String!) {
    service(id: $graphId) {
      id
      variant(name: $variant) {
        id
        latestLaunch {
          id
        }
      }
    }
  }
`;

export interface ContractLaunchDetailsModalState {
  graphId?: string;
  graphTitle?: string;
  contractVariant?: string;
  sourceVariant?: string;
  launchId?: string;
  initiateLaunch?: boolean;
  includedTags?: Set<string>;
  excludedTags?: Set<string>;
  hideUnreachableTypes?: boolean;
}

interface Props {
  isOpen: boolean;
  state: ContractLaunchDetailsModalState | null;
  onClose: () => void;
  workflow: ContractConfigWorkflow;
}

export const ContractLaunchDetailsModal = ({
  isOpen,
  state,
  onClose,
  workflow,
}: Props) => {
  const graphId = state?.graphId;
  const sourceVariant = state?.sourceVariant;
  const contractVariant = state?.contractVariant;

  if (!graphId || !sourceVariant || !contractVariant) {
    // Ideally we wouldn't thrown but react doesn't permit
    // conditionally calling useQuery so we throw instead.
    // If one of these values is undefined we are most likely
    // in a really bad modal state.
    throw new Error(
      'Invalid modal state. Please try again or contact Apollo support.',
    );
  }

  const { data, loading, error } = useQuery(latestLaunchDetailsQuery, {
    variables: {
      graphId,
      variant: contractVariant,
    },
    skip: !state?.initiateLaunch,
  });

  const latestLaunch = data?.service?.variant?.latestLaunch;

  const showHideUnreachableTypes = useLDFlag(
    'mercury_show_hide_unreachable_types_toggle',
  );
  return (
    <Modal isOpen={isOpen} size="2xl" onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <ContractConfigHeader
            currentStep={
              workflow === ContractConfigWorkflow.create
                ? ContractConfigStep.ContractCreated
                : ContractConfigStep.ContractUpdated
            }
            workflow={workflow}
          />
          <ModalTitle className="flex items-center justify-center">
            <IconSuccessSolid className="mr-2 h-5 text-icon-success" />
            Contract Successfully{' '}
            {workflow === ContractConfigWorkflow.create ? 'Created' : 'Updated'}
          </ModalTitle>
          <ModalDescription className="text-center">
            {state?.initiateLaunch ? (
              <>
                Your contract was{' '}
                {workflow === ContractConfigWorkflow.create
                  ? 'created'
                  : 'updated'}{' '}
                and a launch has been initiated.
              </>
            ) : (
              <>
                Your contract was{' '}
                {workflow === ContractConfigWorkflow.create
                  ? 'created'
                  : 'updated'}{' '}
                but a launch was not initiated.
                <br />
                {workflow === ContractConfigWorkflow.edit ? (
                  <>
                    The schema for{' '}
                    <span className="font-semibold">
                      {graphId}@{contractVariant}{' '}
                    </span>{' '}
                    will remain fixed to its last successful build.
                  </>
                ) : null}
              </>
            )}
          </ModalDescription>
        </ModalHeader>
        <ModalBody>
          {loading ? (
            <div className="flex flex-col items-center justify-center">
              <p className="pb-2 text-secondary">Fetching launch details...</p>
              <Spinner size="sm" />
            </div>
          ) : error ? (
            <ErrorMessage>
              Something went wrong. Please refresh and try again.
            </ErrorMessage>
          ) : (
            <DefinitionList
              list={[
                {
                  key: 'graph',
                  name: 'Graph',
                  value: (
                    <div className="flex items-center">
                      <IconGraphQL className="mr-2 text-indigo-light" />
                      {state?.graphTitle}
                    </div>
                  ),
                },
                {
                  key: 'contract',
                  name: 'Contract Name',
                  value: <>{contractVariant}</>,
                },
                {
                  key: 'source',
                  name: 'Source Variant',
                  value: <>{sourceVariant}</>,
                },
                {
                  key: 'excluded',
                  name: 'Excluded Tags',
                  value:
                    (state?.excludedTags?.size ?? 0) > 0 ? (
                      Array.from(state?.excludedTags ?? []).map((tag) => (
                        <span
                          key={tag}
                          className="rounded-sm bg-silver px-2 py-px text-sm font-semibold text-secondary"
                        >
                          {tag}
                        </span>
                      ))
                    ) : (
                      <span className="text-secondary">None</span>
                    ),
                },
                {
                  key: 'included',
                  name: 'Included Tags',
                  value:
                    (state?.includedTags?.size ?? 0) > 0 ? (
                      Array.from(state?.includedTags ?? []).map((tag) => (
                        <span
                          key={tag}
                          className="rounded-sm bg-silver px-2 py-px text-sm font-semibold text-secondary"
                        >
                          {tag}
                        </span>
                      ))
                    ) : (
                      <span className="text-secondary">None</span>
                    ),
                },
                {
                  key: 'hideUnreachableTypes',
                  name: 'Hide unreachable types',
                  value: (
                    <span className="rounded-sm bg-silver px-2 py-px text-sm font-semibold text-secondary">
                      {Boolean(state?.hideUnreachableTypes).toString()}
                    </span>
                  ),
                  hide: !showHideUnreachableTypes,
                },
              ]}
            />
          )}
        </ModalBody>
        <ModalFooter>
          <ButtonGroup>
            <Button variant="secondary" type="button" onClick={onClose}>
              Close
            </Button>
            {state?.initiateLaunch ? (
              <Button
                variant="primary"
                as={Link}
                leftIcon={<IconRocket />}
                to={launchesRouteConfig.location({
                  graphId,
                  graphVariant: contractVariant,
                  launchId: latestLaunch?.id,
                })}
              >
                View launch details
              </Button>
            ) : null}
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
